import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TableOptions } from "../../../../shared-kernel/components/Table";
import {
  createAdapterToBank,
  createAdapterToBankBCRA,
  createAdapterToBankEndpoint,
  createAdapterToFieldBankEndpoint
} from "../adapters/bank.adapter";
import { Bank, BankBCRAEndpoint, BankEndpoint, EndpointBankFiltered } from "../domain/Bank.model";
import { appConfig } from "../../../../config";
import { EndpointBank } from "../domain/Bank.model";
import { createAdapterToFilterBackend } from "../../../../utilities/paramsAdapter";

const BASE_BANK_URL = '/entidadesfinancieras/bancos'
const BASE_BANK_MUTATION_URL = '/entidadesfinancieras/banco'
export const BASE_EXPORT_URL = `${appConfig.API_NODE_URL}/entidadesfinancieras/bancos/export`;

export const bankApi = createApi({
  reducerPath: "bankApi",
  baseQuery: fetchBaseQuery({
    baseUrl: appConfig.API_NODE_URL,
    prepareHeaders: (headers: Headers) => {
      const APP_TOKEN = "app_token";
      const token = localStorage.getItem(APP_TOKEN);

      if (token) {
        headers.set("authorization", `${token}`);
      }

      return headers;
    },
    credentials: "include",
  }),
  refetchOnMountOrArgChange: true,
  tagTypes: ["Banks", "Bank"],
  endpoints: (builder) => ({
    getBank: builder.query<Bank, number | undefined>({
      query: (id) => `${BASE_BANK_MUTATION_URL}/${id}`,
      transformResponse: (response: EndpointBank) => {
        return createAdapterToBank(response.Content)
      },
      providesTags: (_res, _err, id) => [{type: "Bank", id: id?.toString()}],
    }),
    getBanks: builder.query<any, TableOptions<Bank>|undefined>({
      query: (params: TableOptions<Bank>) => {
        const { page, pageSize, order, search, filters } = params || {};
        const adaptersFilter = filters?.map( f => createAdapterToFilterBackend(f,createAdapterToFieldBankEndpoint))
        const serializedFilters = adaptersFilter ? JSON.stringify(adaptersFilter) : undefined;
        const serializedOrder = order ? JSON.stringify({field:createAdapterToFieldBankEndpoint(order.field), order:order.order}) : undefined;
        return {
          url:`${BASE_BANK_URL}/filtros`,
          params: {
            page:page? page - 1: 0,
            pageSize,
            order: serializedOrder,
            search,
            filters:serializedFilters
          },
        }
      },
      providesTags: ["Banks"],
      transformResponse: (response: EndpointBankFiltered, _meta, params: TableOptions<Bank>) => {
        const page = params?.page ?? 1;
        const endpointResponse = response?.rows?.map((bank: BankEndpoint) =>
            createAdapterToBank(bank)
        );
        return {
          data: endpointResponse,
          page,
          total: response.total,
        };
      },
    }),
    getBanksBCRA: builder.query<any, any>({
      query: () => ({
        url: `${BASE_BANK_URL}-bcra`
      }),
      transformResponse: (response: any) => {
        const adaptedResponse = response?.Content?.map((bank: BankBCRAEndpoint) => createAdapterToBankBCRA(bank));
        return {
          data: adaptedResponse
        }
      }
    }),
    createBank: builder.mutation<any, Bank>({
      query: (data) => ({
        url: `${BASE_BANK_MUTATION_URL}`,
        method: "POST",
        body: {
          "Content": createAdapterToBankEndpoint(data)
        },
      }),
      invalidatesTags: ["Banks"],
    }),
    updateBank: builder.mutation<any, Bank>({
      query: (data: Bank) => ({
        url: `${BASE_BANK_MUTATION_URL}`,
        method: "PUT",
        body: {
          "Content": createAdapterToBankEndpoint(data)
        },
      }),
      invalidatesTags: (_res, _err, data: Bank) => ["Banks", {type: 'Bank', id: data?.id?.toString()}],
    }),
    deleteBank: builder.mutation<void, number>({
      query: (id) => ({
        url: `${BASE_BANK_MUTATION_URL}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Banks"],
    }),
  }),
});

export const {
  useGetBankQuery,
  useGetBanksQuery,
  useGetBanksBCRAQuery,
  useCreateBankMutation,
  useUpdateBankMutation,
  useDeleteBankMutation
} = bankApi;
